import type {
  FileAttachmentPart,
  MessagePart,
  SubmittedFileInfo,
  ToolCallPart,
  ToolCategory,
  ToolResultPart,
} from './task-detail-types';

export type { ToolMessageGroup } from '@kyumo/shared-ui';
// 共通ユーティリティは @kyumo/shared-ui から re-export
export { downloadFromUrl, formatFileSize, groupToolMessages } from '@kyumo/shared-ui';

/**
 * パートがツール呼び出しパートかどうかを判定
 */
export function isToolCallPart(part: MessagePart): part is ToolCallPart {
  return part.type === 'tool-call';
}

/**
 * パートがツール結果パートかどうかを判定
 */
export function isToolResultPart(part: MessagePart): part is ToolResultPart {
  return part.type === 'tool-result';
}

/**
 * パートがファイル添付パートかどうかを判定
 */
export function isFileAttachmentPart(part: MessagePart): part is FileAttachmentPart {
  return part.type === 'file-attachment' && 'name' in part;
}

/**
 * sandbox__submitFile の結果をパースして提出ファイル情報を取得
 */
export function parseFileSubmissionResult(
  toolName: string,
  result: unknown,
): { files: SubmittedFileInfo[]; message: string | undefined } | null {
  if (toolName !== 'sandbox__submitFile') {
    return null;
  }

  if (typeof result !== 'object' || result === null) {
    return null;
  }

  const data = result as Record<string, unknown>;

  const fileArray = data.submittedFiles ?? data.files;
  if (data.success !== true || !Array.isArray(fileArray)) {
    return null;
  }

  const files: SubmittedFileInfo[] = [];
  for (const file of fileArray) {
    if (
      typeof file === 'object' &&
      file !== null &&
      'submissionId' in file &&
      'name' in file &&
      'mimeType' in file
    ) {
      const f = file as Record<string, unknown>;
      const size = typeof f.size === 'number' && !Number.isNaN(f.size) ? f.size : 0;
      const s3Key = typeof f.s3Key === 'string' ? f.s3Key : undefined;
      files.push({
        submissionId: String(f.submissionId),
        name: String(f.name),
        mimeType: String(f.mimeType),
        size,
        ...(s3Key && { s3Key }),
      });
    }
  }

  return files.length > 0
    ? { files, message: typeof data.message === 'string' ? data.message : undefined }
    : null;
}

/**
 * ツールの状態をステータスに変換
 */
export function getToolStatus(
  status?: 'running' | 'completed' | 'failed' | 'cancelled',
): 'running' | 'completed' | 'failed' | 'cancelled' {
  return status || 'running';
}

/**
 * カテゴリラベルを取得
 */
export function getCategoryLabel(category?: ToolCategory): string {
  switch (category) {
    case 'builtin':
      return 'ビルトイン';
    case 'manage':
      return '管理';
    case 'sandbox':
      return 'サンドボックス';
    case 'skill':
      return 'スキル';
    case 'subagent':
      return 'エージェント';
    case 'mcp':
      return 'MCP';
    case 'external':
      return '外部連携';
    case 'collaboration':
      return '協調';
    default:
      return 'ツール';
  }
}

/**
 * カテゴリに応じたバッジの色を取得
 */
export function getCategoryBadgeColor(category?: ToolCategory): string {
  // desktop-appのcategory-theme.tsに合わせた色定義
  switch (category) {
    case 'builtin':
      return 'bg-slate-500/20 text-slate-600 dark:text-slate-300';
    case 'manage':
      return 'bg-teal-500/30 text-teal-600 dark:text-teal-300 border border-teal-500/30';
    case 'sandbox':
      return 'bg-pink-500/30 text-pink-600 dark:text-pink-300 border border-pink-500/30';
    case 'skill':
      return 'bg-purple-500/30 text-purple-600 dark:text-purple-300 border border-purple-500/30';
    case 'subagent':
      return 'bg-blue-500/30 text-blue-600 dark:text-blue-300 border border-blue-500/30';
    case 'mcp':
      return 'bg-green-500/30 text-green-600 dark:text-green-300 border border-green-500/30';
    case 'external':
      return 'bg-orange-500/30 text-orange-600 dark:text-orange-300 border border-orange-500/30';
    case 'collaboration':
      return 'bg-indigo-500/30 text-indigo-600 dark:text-indigo-300 border border-indigo-500/30';
    default:
      return 'bg-gray-500/20 text-gray-600 dark:text-gray-400';
  }
}

/**
 * ツールパートを統合してグループ化する
 * tool-call と tool-result を toolCallId でマッチングし、統合されたツール情報の配列を返す
 */
export function groupToolParts(parts: MessagePart[]): {
  /** 統合済みツールパート以外（テキスト、推論など） */
  nonToolParts: { index: number; part: MessagePart }[];
  /** ツール呼び出しと結果のペア（toolCallIdでグループ化） */
  toolGroups: Map<string, { call?: ToolCallPart; result?: ToolResultPart; index: number }>;
} {
  const nonToolParts: { index: number; part: MessagePart }[] = [];
  const toolGroups = new Map<
    string,
    { call?: ToolCallPart; result?: ToolResultPart; index: number }
  >();

  parts.forEach((part, index) => {
    if (isToolCallPart(part)) {
      const existing = toolGroups.get(part.toolCallId);
      if (existing) {
        existing.call = part;
      } else {
        toolGroups.set(part.toolCallId, { call: part, index });
      }
    } else if (isToolResultPart(part)) {
      const existing = toolGroups.get(part.toolCallId);
      if (existing) {
        existing.result = part;
      } else {
        toolGroups.set(part.toolCallId, { result: part, index });
      }
    } else {
      nonToolParts.push({ index, part });
    }
  });

  return { nonToolParts, toolGroups };
}

/**
 * サブエージェントメッセージを subTaskId でグルーピングする
 *
 * `subagent__delegateParallelTasks` は 1 つのツール呼び出しで複数のサブタスクを
 * 並列実行するため、そのままでは全サブタスクのメッセージが 1 件のカードに
 * まとまってしまう。subTaskId 単位に分けてサブタスクごとのカードとして表示する。
 * subTaskId を持たないメッセージ（並列化前の履歴など）は undefined キーの
 * 1 グループへ集約する。表示順を保つため挿入順が維持される Map を返す。
 */
export function groupSubAgentMessagesBySubTaskId<T>(messages: T[]): Map<string | undefined, T[]> {
  const groups = new Map<string | undefined, T[]>();

  for (const entry of messages) {
    const subTaskId =
      typeof entry === 'object' &&
      entry !== null &&
      'subTaskId' in entry &&
      typeof (entry as { subTaskId?: unknown }).subTaskId === 'string'
        ? (entry as { subTaskId: string }).subTaskId
        : undefined;

    const group = groups.get(subTaskId);
    if (group) {
      group.push(entry);
    } else {
      groups.set(subTaskId, [entry]);
    }
  }

  return groups;
}
